<template>
  <mc-sidepanel :active="!!questions.questionId" @close="closeHandler">
    <template #title>
      <div class="level">
        <div class="level-left">
          <div class="level-item">
            <div class="title is-5 is-marginless">
              Edit Question
            </div>
          </div>
          <div class="level-item">
            <b-taglist
              v-if="
                !!questions.questionId && !questions.loadingSelected && fragile
              "
              attached
            >
              <b-tag type="is-dark">Version</b-tag>
              <b-tag type="is-info">
                {{ questions.selected.version || 0 }}
              </b-tag>
            </b-taglist>
          </div>
        </div>
        <div class="level-right buttons">
          <b-button
            outlined
            size="is-small"
            type="is-primary"
            @click="cloneQuestionHandler"
          >
            Clone a Question
          </b-button>
          <b-button
            outlined
            size="is-small"
            type="is-primary"
            @click="insertTemplateHandler"
          >
            Insert Template
          </b-button>
        </div>
      </div>
    </template>
    <template #default>
      <b-loading
        v-if="questions.loadingSelected"
        active
        :is-full-page="false"
      />
      <mc-error-layout
        v-else-if="questions.selectError"
        message="Failed to load question!"
        :retry-fn="() => selectQuestion(questions.questionId)"
      />
      <div v-else class="edit-question section">
        <div v-if="validationErrors" class="content has-text-danger">
          <p>Please fix these errors:</p>
          <ol type="1">
            <li v-for="(error, index) in validationErrors" :key="index">
              {{ error }}
            </li>
          </ol>
        </div>
        <div class="edit-question__flow">
          <FlowEditor
            :flow-definition="questions.selected.flowDefinition"
            :on-submit="submitHandler"
            :submitting="questions.submitting"
          />
        </div>
      </div>
    </template>
  </mc-sidepanel>
</template>

<script>
import { mapState, mapActions } from 'vuex';

import { MCValidationError } from '../firebase';
import questionsApi from '../firebase/questions';

import con from '../store/mutationTypes';
import FlowEditor from './chat/FlowEditor';
import { generateTemplate } from './modals/FlowTemplateGeneratorModal.vue';
import { openQuestionSearchModal } from './modals/QuestionSearchModal.vue';

export default {
  data() {
    return {
      validationErrors: null
    };
  },
  computed: {
    ...mapState(['questions']),
    fragile() {
      return this.questions.selected.ownerId.path.startsWith('tests');
    }
  },
  methods: {
    ...mapActions('questions', [
      'selectQuestion',
      'updateQuestion',
      'cloneQuestion',
      'validateQuestion'
    ]),
    closeHandler() {
      this.$store.commit(con.QUESTION_EDITOR_SET_NAMESPACED, {});
    },
    isABreakingChange() {
      return new Promise((resolve, reject) => {
        this.$buefy.dialog.confirm({
          title: 'Is this a breaking change?',
          message: `Do your edits make this a different question or is it just
          a spelling correction kind of thing which won't change the meaning or
          the flow of the question?`,
          onConfirm: () => resolve(true),
          onCancel: e =>
            e == 'button' ? resolve(false) : reject(new Error('canceled')),
          type: 'is-danger',
          confirmText: 'Yes',
          cancelText: 'No'
        });
      });
    },
    async insertTemplateHandler() {
      try {
        this.questions.selected.flowDefinition = await generateTemplate(this);
        this.$buefy.toast.open({
          message: 'Template Generated successfully.',
          type: 'is-success'
        });
      } catch {
        this.$buefy.toast.open({
          message: 'Template generation canceled.',
          type: 'is-danger'
        });
      }
    },
    async submitHandler(definition) {
      try {
        const updateData = {
          id: this.questions.selected.id,
          flowDefinition: definition
        };
        this.validationErrors = null;
        this.validateQuestion(updateData);
        const currentVersion = this.questions.selected.version || 0;
        const upgradeVersion = this.fragile
          ? await this.isABreakingChange()
          : false;
        updateData.version = upgradeVersion
          ? currentVersion + 1
          : currentVersion;
        await this.updateQuestion(updateData);
        this.$buefy.toast.open({
          message: 'Question updated successfully!',
          type: 'is-success'
        });
      } catch (error) {
        if (error instanceof MCValidationError) {
          this.validationErrors = error.errors;
        } else {
          this.$buefy.toast.open({
            message:
              error.message == 'canceled'
                ? 'Question update canceled!'
                : 'Failed to update question!',
            type: 'is-danger'
          });
        }
      }
    },
    async cloneQuestionHandler() {
      try {
        const searchedQuestion = await openQuestionSearchModal(this);
        await new Promise(resolve => {
          this.$buefy.dialog.confirm({
            title: `Check the statement below and confirm?`,
            // TODO: this prop can take html, follwing statement can be
            // parsed with markdown parser before passing.
            message: searchedQuestion.statement,
            cancelText: 'Cancel',
            confirmText: 'Yes! Continue',
            type: 'is-success',
            onConfirm: () => {
              try {
                this.cloneQuestion(searchedQuestion.id);
                this.$buefy.toast.open({
                  message: 'Question updated successfully.',
                  type: 'is-success'
                });
                resolve();
              } catch {
                this.$buefy.toast.open({
                  message: 'Cloning question failed!',
                  type: 'is-danger'
                });
              }
            }
          });
        });
      } catch {
        this.$buefy.toast.open({
          message: 'Question search canceled!',
          type: 'is-danger'
        });
      }
    }
  },
  components: {
    FlowEditor
  }
};
</script>

<style lang="scss" scoped>
.edit-question {
  position: relative;
}
</style>
